<div class="ion-page" *ngIf="componentLoaded">
  <!--
    Not an ion-toolbar with color="tertiary": that paints the editor's own header with the
    brand colour being edited, so the tool changed appearance as the brand did.
  -->
  <header class="panel-header">
    <span class="panel-title">{{ "commons.themeConfiguration.title" | translate }}</span>
    <span class="panel-scope" *ngIf="programCode" [title]="domain">{{ programCode }}</span>
  </header>

  <ion-content aria-label="Theme configurator">
    <div class="panel-body">

      <app-brand-panel *ngIf="brandLoaded"></app-brand-panel>

      <form *ngIf="brandLoaded" class="settings-form" [formGroup]="colorForm">

        <!-- Images -->
        <section class="panel-section">
          <h6 class="section-title" (click)="toggleSection('images')">
            <ion-icon [name]="isOpen('images') ? 'chevron-down-outline' : 'chevron-forward-outline'"></ion-icon>
            {{ "commons.themeConfiguration.sections.images" | translate }}
          </h6>

          <div class="section-body" *ngIf="isOpen('images')">
            <div class="media-row" *ngFor="let slot of imageSlots">
              <div class="thumb" [class.empty]="!pathOf(slot)">
                <img *ngIf="pathOf(slot)" [src]="previewOf(slot)" [alt]="slot.label" />
                <ion-icon *ngIf="pathOf(slot)" class="remove" name="close-circle"
                  [title]="'commons.themeConfiguration.buttons.removeImage' | translate"
                  (click)="removeFile(slot.key)"></ion-icon>
              </div>

              <div class="media-text">
                <span class="field-label">{{ ("commons.themeConfiguration.files." + slot.label) | translate }}</span>
                <span class="field-hint">{{ slot.hint }}</span>
              </div>

              <label class="icon-action" [title]="'commons.themeConfiguration.buttons.addChangeImage' | translate">
                <ion-icon name="cloud-upload-outline"></ion-icon>
                <input type="file" [accept]="slot.accept" (change)="onFilePicked(slot, $event)" />
              </label>
            </div>

            <!-- Marketing banners are a set rather than a single slot. -->
            <div class="media-row">
              <div class="media-text">
                <span class="field-label">{{ "commons.themeConfiguration.files.marketingBanner" | translate }}</span>
                <span class="field-hint">{{ "commons.themeConfiguration.files.marketingBannerHint" | translate }}</span>
              </div>

              <label class="icon-action" [title]="'commons.themeConfiguration.buttons.addImage' | translate">
                <ion-icon name="cloud-upload-outline"></ion-icon>
                <input type="file" multiple accept=".png, .jpg, .jpeg, .webp, .svg"
                  (change)="onFilePicked(marketingSlot, $event, true)" />
              </label>
            </div>

            <div class="banner-strip" *ngIf="marketingBannerImages?.length">
              <div class="banner" *ngFor="let marketingImage of marketingBannerImages">
                <img [src]="marketingImage.url" [alt]="marketingImage.name" />
                <ion-icon class="remove" name="close-circle" (click)="removeMarketingFile(marketingImage.path)"
                  [title]="'commons.themeConfiguration.buttons.removeImage' | translate"></ion-icon>
              </div>
            </div>
          </div>
        </section>

        <!-- Documents -->
        <section class="panel-section">
          <h6 class="section-title" (click)="toggleSection('documents')">
            <ion-icon [name]="isOpen('documents') ? 'chevron-down-outline' : 'chevron-forward-outline'"></ion-icon>
            {{ "commons.themeConfiguration.sections.documents" | translate }}
          </h6>

          <div class="section-body" *ngIf="isOpen('documents')">
            <div class="field" *ngFor="let field of documentFields">
              <!-- The label truncates, so the answer about the link sits outside it and never does. -->
              <div class="field-head">
                <span class="field-label">{{ ("commons.themeConfiguration.files." + field.label) | translate }}</span>

                <span class="link-state" [ngClass]="linkState(field.control)" *ngIf="linkState(field.control)"
                  [title]="'commons.themeConfiguration.documentLink.' + linkState(field.control) | translate">
                  <ion-icon *ngIf="linkState(field.control) === 'ok'" name="checkmark-circle"></ion-icon>
                  <ion-icon *ngIf="linkState(field.control) === 'broken'" name="alert-circle"></ion-icon>
                  <ion-icon *ngIf="linkState(field.control) === 'checking'" name="ellipsis-horizontal"></ion-icon>
                  <span *ngIf="linkState(field.control) === 'broken'">{{ linkDetail(field.control) }}</span>
                </span>
              </div>

              <input type="text" [formControlName]="field.control + 'Input'" />
            </div>
          </div>
        </section>

        <!-- Program -->
        <section class="panel-section">
          <h6 class="section-title" (click)="toggleSection('program')">
            <ion-icon [name]="isOpen('program') ? 'chevron-down-outline' : 'chevron-forward-outline'"></ion-icon>
            {{ "commons.themeConfiguration.sections.program" | translate }}
          </h6>

          <div class="section-body" *ngIf="isOpen('program')">
            <div class="field">
              <span class="field-label">{{ "commons.themeConfiguration.settings.programName" | translate }}</span>
              <input type="text" formControlName="programNameInput" />
            </div>

            <div class="field">
              <span class="field-label">{{ "commons.themeConfiguration.settings.bankSupportPhoneNumber" | translate }}</span>
              <input type="text" formControlName="bankSupportPhoneNumberInput" />
            </div>
          </div>
        </section>

        <!-- Footer -->
        <section class="panel-section">
          <h6 class="section-title" (click)="toggleSection('footer')">
            <ion-icon [name]="isOpen('footer') ? 'chevron-down-outline' : 'chevron-forward-outline'"></ion-icon>
            {{ "commons.themeConfiguration.sections.footer" | translate }}
          </h6>

          <div class="section-body" *ngIf="isOpen('footer')">
            <div class="field">
              <span class="field-label">{{ "commons.themeConfiguration.settings.footerCopy" | translate }}</span>
              <textarea formControlName="footerCopyInput" rows="6"></textarea>
            </div>
          </div>
        </section>

        <!-- Contact us -->
        <section class="panel-section">
          <h6 class="section-title" (click)="toggleSection('contactUs')">
            <ion-icon [name]="isOpen('contactUs') ? 'chevron-down-outline' : 'chevron-forward-outline'"></ion-icon>
            {{ "commons.themeConfiguration.sections.contactUs" | translate }}
          </h6>

          <div class="section-body" *ngIf="isOpen('contactUs')">
            <div class="field">
              <span class="field-label">{{ "commons.themeConfiguration.settings.contactUsTitleText" | translate }}</span>
              <textarea formControlName="contactUsTitleTextInput" rows="2"></textarea>
            </div>

            <div class="field">
              <span class="field-label">{{ "commons.themeConfiguration.settings.contactUsText" | translate }}</span>
              <textarea formControlName="contactUsTextInput" rows="6"></textarea>
            </div>
          </div>
        </section>

        <!-- Operator site link. The toggle enables the three fields under it. -->
        <section class="panel-section">
          <h6 class="section-title" (click)="toggleSection('operatorLink')">
            <ion-icon [name]="isOpen('operatorLink') ? 'chevron-down-outline' : 'chevron-forward-outline'"></ion-icon>
            {{ "commons.themeConfiguration.sections.operatorLink" | translate }}
          </h6>

          <div class="section-body" *ngIf="isOpen('operatorLink')">
            <label class="toggle first">
              <input type="checkbox" formControlName="isOperatorSiteLinkEnabledInput" />
              <span>{{ "commons.themeConfiguration.settings.isOperatorSiteLinkEnabled" | translate }}</span>
            </label>

            <div class="field">
              <span class="field-label">{{ "commons.themeConfiguration.settings.operatorSiteLinkVerbiage" | translate }}</span>
              <textarea formControlName="operatorSiteLinkVerbiageInput" rows="2"></textarea>
            </div>

            <div class="field">
              <span class="field-label">{{ "commons.themeConfiguration.settings.operatorSiteLinkText" | translate }}</span>
              <input type="text" formControlName="operatorSiteLinkTextInput" />
            </div>

            <div class="field">
              <span class="field-label">{{ "commons.themeConfiguration.settings.operatorSiteLinkUrl" | translate }}</span>
              <input type="text" formControlName="operatorSiteLinkUrlInput" />
            </div>
          </div>
        </section>

        <!-- External bank transfer -->
        <section class="panel-section">
          <h6 class="section-title" (click)="toggleSection('bankTransfer')">
            <ion-icon [name]="isOpen('bankTransfer') ? 'chevron-down-outline' : 'chevron-forward-outline'"></ion-icon>
            {{ "commons.themeConfiguration.sections.bankTransfer" | translate }}
          </h6>

          <div class="section-body" *ngIf="isOpen('bankTransfer')">
            <label class="toggle first">
              <input type="checkbox" formControlName="isTransferToBankEnabledInput" />
              <span>{{ "commons.themeConfiguration.settings.isTransferToBankEnabled" | translate }}</span>
            </label>

            <div class="field">
              <span class="field-label">{{ "commons.themeConfiguration.settings.externalBankTransferInfoHeader" | translate }}</span>
              <textarea formControlName="externalBankTransferInfoHeaderInput" rows="2"></textarea>
            </div>

            <div class="field">
              <span class="field-label">{{ "commons.themeConfiguration.settings.externalBankTransferInfoBody" | translate }}</span>
              <textarea formControlName="externalBankTransferInfoBodyInput" rows="5"></textarea>
            </div>
          </div>
        </section>

        <!-- IntraFi -->
        <section class="panel-section">
          <h6 class="section-title" (click)="toggleSection('intraFi')">
            <ion-icon [name]="isOpen('intraFi') ? 'chevron-down-outline' : 'chevron-forward-outline'"></ion-icon>
            {{ "commons.themeConfiguration.sections.intraFi" | translate }}
          </h6>

          <div class="section-body" *ngIf="isOpen('intraFi')">
            <div class="field">
              <span class="field-label">{{ "commons.themeConfiguration.settings.intraFiDescription" | translate }}</span>
              <textarea formControlName="intraFiDescriptionInput" rows="5"></textarea>
            </div>

            <div class="field">
              <span class="field-label">{{ "commons.themeConfiguration.settings.intraFiOptOutBalanceMessage" | translate }}</span>
              <textarea formControlName="intraFiOptOutBalanceMessageInput" rows="3"></textarea>
            </div>

            <div class="field">
              <span class="field-label">{{ "commons.themeConfiguration.settings.intraFiOptOutSweptBalanceMessage" | translate }}</span>
              <textarea formControlName="intraFiOptOutSweptBalanceMessageInput" rows="3"></textarea>
            </div>
          </div>
        </section>

        <!-- Write-off messages -->
        <section class="panel-section">
          <h6 class="section-title" (click)="toggleSection('writeOff')">
            <ion-icon [name]="isOpen('writeOff') ? 'chevron-down-outline' : 'chevron-forward-outline'"></ion-icon>
            {{ "commons.themeConfiguration.sections.writeOff" | translate }}
          </h6>

          <div class="section-body" *ngIf="isOpen('writeOff')">
            <label class="toggle first">
              <input type="checkbox" formControlName="emulateCustomerHasWriteOffInput" />
              <span>{{ "commons.themeConfiguration.settings.emulateCustomerHasWriteOff" | translate }}</span>
            </label>

            <p class="section-note">
              {{ "commons.themeConfiguration.settings.emulateCustomerHasWriteOffHelpText" | translate }}
            </p>

            <div class="field">
              <span class="field-label">{{ "commons.themeConfiguration.settings.homeAvailableBalanceWriteOffTitle" | translate }}</span>
              <textarea formControlName="homeAvailableBalanceWriteOffTitleInput" rows="2"></textarea>
            </div>

            <div class="field">
              <span class="field-label">{{ "commons.themeConfiguration.settings.homeAvailableBalanceWriteOffText" | translate }}</span>
              <textarea formControlName="homeAvailableBalanceWriteOffTextInput" rows="3"></textarea>
            </div>

            <div class="field">
              <span class="field-label">{{ "commons.themeConfiguration.settings.homeAvailableBalanceWriteOffButtonText" | translate }}</span>
              <input type="text" formControlName="homeAvailableBalanceWriteOffButtonTextInput" />
            </div>

            <div class="field">
              <span class="field-label">{{ "commons.themeConfiguration.settings.homeAvailableBalanceWriteOffHelpText" | translate }}</span>
              <textarea formControlName="homeAvailableBalanceWriteOffHelpTextInput" rows="3"></textarea>
            </div>

            <div class="field">
              <span class="field-label">{{ "commons.themeConfiguration.settings.activityDetailsWriteOffWarningText" | translate }}</span>
              <textarea formControlName="activityDetailsWriteOffWarningTextInput" rows="3"></textarea>
            </div>
          </div>
        </section>
      </form>
    </div>
  </ion-content>
</div>
